import { useState } from "react";
import { useAppDispatch } from "../redux/store";
import { getBlocks } from "../redux/Reducers/blocksReducer";
import { T } from "react-translator-component";
import appServices from "../services/app-services/appServices";
import LoadingModal from "./LoadingModal";
import MessageModal from "./Models/GreenCity/MessageModal";
import TwoButtonModal from "./TwoButtonModal";
import { colors as c } from "../common/colors";

interface Props {
  open: boolean;
  currentUserId: number;
  isGroupPost?: boolean;
  blockId?: number | null;
  targetId: number;
  onContinue?: () => void;
  onCancel?: () => void;
  onClose: () => void;
}

export default function BlockModal({
  open,
  currentUserId,
  isGroupPost = false,
  blockId = null,
  targetId,
  onContinue,
  onCancel,
  onClose
}: Props) {

  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [openMessageModal, setOpenMessageModal] = useState<boolean>(false)
  const [modalMessage, setModalMessage] = useState<string>("")

  const handleModalContinue = async () => {
    const sendApiResult = blockId ? await unBlocked(blockId) : await blocked()
    return sendApiResult
  }

  const blocked = async () => {
    const body = {
      blocked_id: targetId,
      blocked_type: isGroupPost ? "MemberGroup" : "User",
    }
    try {
      setIsLoading(true)
      await appServices.createBlock(body)
      await dispatch(getBlocks(currentUserId))
      setOpenMessageModal(true)
      setModalMessage(`${T("blocked")}`)
      if (onContinue) onContinue()
      return true
    } catch (error: any) {
      setOpenMessageModal(true)
      setModalMessage(error?.response?.data?.message ?? T("api_unknown_error"))
      return false
    } finally {
      setIsLoading(false)
      onClose()
    }
  }

  const unBlocked = async (blockId: number) => {
    try {
      setIsLoading(true)
      await appServices.deleteBlock(blockId)
      await dispatch(getBlocks(currentUserId))
      setOpenMessageModal(true)
      setModalMessage(`${T("unblocked")}`)
      if (onContinue) onContinue()
      return true
    } catch (error: any) {
      if (error?.response?.status === 404) await dispatch(getBlocks(currentUserId))
      setOpenMessageModal(true)
      setModalMessage(error?.response.data.message)
      return false
    } finally {
      setIsLoading(false)
      onClose()
    }
  }

  const showModalTitle = () => {
    return blockId ? `${T("confirm_unblock")}` : `${T("confirm_block")}`
  }

  const showModalContent = () => {
    return blockId ? `${T("unblock_notice")}` : `${T("block_notice")}`
  }

  const closeConfirmationModal = () => {
    onCancel ? onCancel() : onClose()
    setOpenMessageModal(false)
  }

  const closeMessageModal = () => {
    onClose()
    setOpenMessageModal(false)
  }

  return (
    <>
      <LoadingModal visible={isLoading} />
      <TwoButtonModal
        open={open}
        message={showModalTitle()}
        messageDetail={showModalContent()}
        onClose={() => closeConfirmationModal()}
        onContinue={() => handleModalContinue()}
        noText={`${T("back_kanji")}`}
        btnStyle={styles.btnStyle}
      />

      <MessageModal
        open={openMessageModal}
        onClose={() => closeMessageModal()}
        onContinue={()=>{}}
        message={modalMessage}
        showButton={true}
        buttonText={`${T('close')}`}
        buttonStyles={styles.dialogButton}
      />
    </>
  );
}

const styles = {
  modalContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "85%",
    height: "280px",
    maxWidth: "320px",
    padding: "0 10px",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "10px",
    transition: "opacity 0.3s ease-in-out",
    textAlign: "center"
  },

  modalMessage: {
    position: 'relative',
    top: "40%",
  },

  dialogButton: {
    width: "50%",
    marginTop: "30px",
    minWitdh: "100px",
    alignSelf: "center",
    display: "inline-block",
    border: "3px solid",
    borderRadius: "10px",
    borderColor: c.farm_blue200,
    color: c.farm_black,
    position: 'relative',
    top: "50%",
  },

  btnStyle: {
    cursor: "pointer",
    width: "50%",
    textAlign: "center",
    border: "0.5px solid gray",
    borderRight: "none",
    borderRadius: 0,
    padding: "20px 10px",
    color: c.farm_blue200,
    fontWeight: "bold"
  }
}